<script>
import Layout from '@layouts/adminLayout.vue'
import MottoIcon from '@components/motto-icon.vue'
import FavoriteHeart from '@components/favorite-heart.vue'
import LoaderIcon from '@components/loader-icon.vue'
import ResourceBlock from '@components/resources/resource-block.vue'
import ModalResource from '@components/resources/modal-resource.vue'
import CopyToClipboardButton from '@components/copy-to-clipboard-button.vue'
import ResourceDownloadFile from '@components/resources/resource-download-file.vue'
import { addFavoriteResource, addResourceViewCount, deleteFavoriteResource, getResourceById } from '@src/api/resources'
import {
  transform as transformResource,
  transformMany as transformResources,
} from '@src/api/transformers/resourceTransformer'
import appConfig from '@src/app.config.json'
import { resourcesComputed } from '@state/helpers'
import _ from 'lodash'

export default {
  page: {
    title: 'Resource',
    meta: [
      {
        name: 'description',
        content: 'The Resource page.',
      },
    ],
  },
  components: {
    Layout,
    MottoIcon,
    FavoriteHeart,
    LoaderIcon,
    ResourceBlock,
    ModalResource,
    CopyToClipboardButton,
    ResourceDownloadFile,
  },
  data() {
    return {
      bvToast: this.toastComponent || this.$bvToast,
      loading: false,
      loadingFavorite: false,
      error: false,
      resource: {},
      youtubeVideoId: null,
      vimeoVideoId: null,
    }
  },
  computed: {
    ...resourcesComputed,

    getAllRelatedResources() {
      if (_.isEmpty(this.allResources)) {
        return []
      }
      return this.allResources
    },

    resourceUrl() {
      return window.location.origin + '/resource/' + this.resource.resourceId
    },
  },
  mounted() {
    this.fetchResource(this.$route.params.resourceId)
  },
  methods: {
    async fetchResource(resourceId) {
      if (!resourceId && this.$route.query.preview) {
        this.fetchResourcePreview()
        return
      }
      this.loading = true
      try {
        const { data: resource } = await getResourceById({ resourceId })
        this.resource = transformResource(resource)
        this.$store.commit('resources/SET_RESOURCES', transformResources(this.resource.relatedResources))
        this.$store.commit('resources/SET_SHOULD_RELOAD_RESOURCES', true)
        document.title = `${resource.title} | ${appConfig.title}`
        this.verifyVideo()
        await addResourceViewCount({ resourceId })
      } catch (err) {
        this.bvToast.toast('There was an error loading this resource. Please try again later.', {
          toaster: 'b-toaster-bottom-center',
          toastClass: 'toast-error',
          noAutoHide: true,
        })
      } finally {
        this.loading = false
      }
    },

    fetchResourcePreview() {
      try {
        const preview = this.$route.query.preview
        const resource = JSON.parse(preview)
        this.resource = transformResource(resource)
        this.resource.resourceId = null
        document.title = `${resource.title} | ${appConfig.title}`
        this.verifyVideo()
      } catch (e) {
        this.bvToast.toast('There was an error loading this resource preview. Please try again.', {
          toaster: 'b-toaster-bottom-center',
          toastClass: 'toast-error',
          noAutoHide: true,
        })
      }
    },

    verifyVideo() {
      if (this.resource.assetType !== 'video' || !this.resource.assetUrl) {
        return
      }
      const youtubeVideoIdFromURL = this.$youtube.getIdFromURL(this.resource.assetUrl)
      if (youtubeVideoIdFromURL !== this.resource.assetUrl) {
        this.youtubeVideoId = youtubeVideoIdFromURL
      }
      if (this.resource.assetUrl.indexOf('vimeo') !== -1) {
        this.vimeoVideoId = this.resource.assetUrl
      }
    },

    async favoriteResource() {
      await addFavoriteResource({ resourceId: this.resource.resourceId })
      this.resource.isFavorite = true
    },

    async unfavoriteResource() {
      await deleteFavoriteResource({ resourceId: this.resource.resourceId })
      this.resource.isFavorite = false
    },

    async handleHeartClick() {
      if (this.loadingFavorite) {
        return
      }
      this.loadingFavorite = true
      if (this.resource.isFavorite) {
        await this.unfavoriteResource()
      } else {
        await this.favoriteResource()
      }
      this.loadingFavorite = false
    },
  },
}
</script>

<template>
  <Layout>
    <LoaderIcon v-if="loading || error" :error="error" />
    <div v-else>
      <div :class="$style.breadcrumbs">
        <div>
          <BaseLink class="p2" :class="$style.breadcrumbLink" :to="{ name: 'resources' }">
            <MottoIcon
              :class="$style.backButton"
              name="Arrow"
              icon-color="gray"
              class="cursor-pointer"
              @click.native="$router.go(-1)" />
          </BaseLink>
        </div>
        <div>
          <BaseLink class="p2" :class="$style.breadcrumbLink" :to="{ name: 'resources' }">RESOURCES</BaseLink>
          <BaseIcon v-if="resource.categoryName" data-breadcrumb-separator class="color-gray" name="chevron-right" />
          <BaseLink
            v-if="resource.categoryName"
            class="p2"
            :class="$style.breadcrumbLink"
            :to="{
              name: 'resources',
              params: { category: resource.categoryName.toLowerCase() },
            }">
            {{ resource.categoryName.toUpperCase() }}
          </BaseLink>
          <BaseIcon v-if="resource.subcategoryName" data-breadcrumb-separator class="color-gray" name="chevron-right" />
          <BaseLink
            v-if="resource.subcategoryName"
            class="p2"
            :class="$style.breadcrumbLink"
            :to="{
              name: 'resources',
              params: {
                category: resource.categoryName.toLowerCase(),
                subcategory: resource.subcategoryName.toLowerCase(),
              },
            }">
            {{ resource.subcategoryName.toUpperCase() }}
          </BaseLink>
        </div>
      </div>
      <div class="d-flex" :class="$style.resourceTitleSection">
        <h2 :class="$style.resourceTitle" class="color-primary-blue">{{ resource.title }}</h2>
        <LoaderIcon v-if="loadingFavorite" class="d-inline" :class="$style.heartIcon" />
        <FavoriteHeart
          v-else-if="resource.resourceId"
          :class="$style.heartIcon"
          :is-favorite="resource.isFavorite"
          @handleHeartClick="handleHeartClick" />
        <CopyToClipboardButton v-if="resource.resourceId" :text-to-copy="resourceUrl" :class="$style.clipboardButton" />
      </div>
      <p class="p1 color-dark-gray">{{ resource.revisedAt }}</p>

      <p :class="$style.resourceTextContent" class="p1 color-primary-charcoal" v-html="resource.content"></p>
      <div v-if="resource.tags && resource.tags.length" :class="$style.resourceTags" class="tagsContainer">
        <span v-for="tag in resource.tags" :key="tag" class="tag-text">{{ tag }}</span>
      </div>

      <youtube
        v-if="youtubeVideoId"
        :class="$style.frameVideo"
        :video-id="youtubeVideoId"
        player-width="864"
        player-height="486"></youtube>

      <vimeo-player
        v-if="vimeoVideoId"
        :class="$style.frameVideo"
        :video-id="vimeoVideoId"
        player-width="864"
        player-height="486" />

      <ResourceDownloadFile v-if="resource.isDownloadable" :resource="resource" />

      <div v-if="getAllRelatedResources.length">
        <hr :class="$style.hr" />
        <div :class="$style.gridView">
          <ResourceBlock
            v-for="relatedResource in getAllRelatedResources"
            :key="relatedResource.resourceId"
            :class="$style.resourceBlock"
            :resource="relatedResource" />
        </div>
      </div>

      <b-modal
        id="view-resource-modal"
        :modal-class="['right', 'modal-resource']"
        hide-footer
        hide-header
        title="BootstrapVue">
        <ModalResource :toast-component="this.$bvToast" />
      </b-modal>
    </div>
  </Layout>
</template>

<style lang="scss" module>
@import '@design';

.breadcrumbs {
  display: flex;
  align-items: center;

  svg[data-breadcrumb-separator] {
    margin: -2px 13px 0;
  }
}

.backButton {
  margin-right: 29px;

  > svg {
    width: 30px;
    height: 30px;
  }
}

.breadcrumbLink {
  font-size: 12px;
  color: $darkest-gray;
  text-decoration: none;
}

.resourceTitleSection {
  margin-top: 28px;
}

.resourceTitle {
  @media (max-width: $lg-breakpoint) {
    font-size: 18px;
  }

  max-width: 690px;
}

.heartIcon {
  @media (max-width: $lg-breakpoint) {
    font-size: 24px;
  }

  width: 24px;
  min-width: 24px;
  height: 24px;
  margin-bottom: 3px;
  margin-left: 42px;
}

.resourceTextContent {
  @media (max-width: $lg-breakpoint) {
    margin-top: 21px;
  }

  margin-top: 32px;
  overflow-x: scroll;
  word-break: break-word;
}

.resourceTags {
  @media (max-width: $lg-breakpoint) {
    margin-top: 8px;
  }

  margin-top: 24px;
}

.frameVideo {
  iframe {
    @media (max-width: $lg-breakpoint) {
      width: 100%;
      border-radius: 11px;
    }
    @media (max-width: $sm-breakpoint) {
      height: 300px;
    }
  }

  margin-top: 48px;
  margin-bottom: 60px;
}

.clipboardButton {
  @media (max-width: $lg-breakpoint) {
    top: 162px;
    right: 24px;
  }

  margin-top: -8px;
  margin-left: 24px;
}

.hr {
  margin-top: 36px;
  margin-bottom: 32px;
  border-color: $lightest-gray;
}

.gridView {
  @media (max-width: $lg-breakpoint) {
    grid-template-columns: repeat(auto-fill, minmax(327px, 1fr));
    grid-gap: 16px 16px;
  }

  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(275px, 1fr));
  grid-gap: 32px 24px;
  margin-top: 24px;
}

.resourceBlock {
  @media (max-width: $lg-breakpoint) {
    width: auto;
  }

  min-width: 275px;
}
</style>
